import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { SurveyGroupDto } from '@/shared/api/types'
import { addWave } from './api'
import { DateTimePicker } from '@/components/erp/date-picker'

/** `datetime-local` speaks local wall-clock with no zone; `opensAt` is an Instant. */
function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * Appending a wave to a tracker.
 *
 * ## There is no wave number on this form, and that is the point
 *
 * `waveNo` is assigned server-side as `max + 1` inside the same transaction that
 * inserts the row, and `WaveRequest` has no component for it — a client-supplied
 * number would not even be read. It must stay that way: two people adding a wave
 * at the same moment would both read "3 waves exist" and both compute 4, and a
 * tracker whose wave numbers repeat cannot be read in order afterwards. The
 * number is the sequence, so only the thing holding the lock may issue it.
 *
 * A wave is append-only. There is no `PUT` and no `DELETE` on `/waves` — the
 * server offers exactly one verb here, so this dialog offers exactly one.
 *
 * ## The window is optional, and its two ends mean different things
 *
 * `opensAt`/`closesAt` describe the FIELD PERIOD the wave is meant to cover. They
 * do not open or close anything: a poll's own status is what decides whether it
 * is collecting. Left empty they are simply not declared, which is honest for a
 * wave whose dates are not settled.
 */
export function WaveDialog({ open, group, onClose, onSaved }: {
  open: boolean
  /** The tracker the wave is appended to. Its `waves` give the count, not the number. */
  group: SurveyGroupDto
  onClose: () => void
  onSaved: (saved: SurveyGroupDto) => void
}) {
  const [label, setLabel] = useState('')
  const [opensAt, setOpensAt] = useState('')
  const [closesAt, setClosesAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLabel('')
    setOpensAt('')
    setClosesAt('')
    setError(null)
  }, [open, group.id])

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      // No `waveNo` in this body. See the note above: the server issues it.
      const saved = await addWave(group.id, {
        label: label.trim() || undefined,
        opensAt: fromLocalInput(opensAt),
        closesAt: fromLocalInput(closesAt),
      })
      onSaved(saved)
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const backwards = !!opensAt && !!closesAt && new Date(closesAt) <= new Date(opensAt)

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title={`Add a wave to ${group.code}`}
      description="Appended to the end of the series. The wave number is issued by the server."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn-primary h-9 text-erp-sm"
            disabled={busy || backwards} onClick={submit}>
            {busy ? 'Adding…' : 'Add wave'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Label"
          hint="What this round is called in conversation. Left blank, the server names it after its number.">
          <input className="erp-input h-9 w-full" value={label} maxLength={200}
            onChange={(e) => setLabel(e.target.value)} placeholder="August wave" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Opens">
            <DateTimePicker value={opensAt} onChange={setOpensAt} className="h-9 w-full" />
          </Field>
          <Field label="Closes"
            error={backwards ? 'The wave closes before it opens.' : undefined}>
            <DateTimePicker value={closesAt} onChange={setClosesAt} className="h-9 w-full" />
          </Field>
        </div>

        <Note tone="info">
          {group.waves.length === 0
            ? 'This is the first wave. The server will number it 1.'
            : `${group.waves.length} wave${group.waves.length === 1 ? '' : 's'} declared so far. `
              + 'The new one is numbered max + 1 inside the transaction that writes it, so two '
              + 'people adding a wave at once cannot both get the same number.'}
        </Note>
      </div>
    </Modal>
  )
}
